import * as React from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useDispatch, useSelector } from "react-redux";
import { setConfig } from "../../index";
import { StatusBall } from "../../../../../Components/Elements/StatusBall";
import { Typography, Tooltip } from "@mui/material";
import useDeepEqualSelector from "../../../../../Hooks/useDeepEqualSelector";
import DownloadIcon from "@mui/icons-material/Download";
import dayjs from "dayjs";

export const MainTable = ({ isLoading, setIsLoading }) => {
    
    const items = useSelector((store) => store.rosterArchive.list);
    const dispatch = useDispatch();
    const config = useDeepEqualSelector((store) => store.rosterArchive.config);

    const handleDownload = React.useCallback(
        (documentId, documentUrl) => () => {
            if (documentUrl) {
                window.open(documentUrl, '_blank');
            }
        },
        [],
    );

    const columns = React.useMemo(
        () => {
            return [
                {
                    field: 'week',
                    headerName: 'Week',
                    type: 'string',
                    flex: 0.7,
                    minWidth: 150,
                    renderCell: (params) => (
                        <Typography sx={{ fontSize: '0.86rem', lineHeight: 'inherit' }}>
                            {params.row.roster ? `Week ${params.row.roster.week} - ${dayjs(params.row.roster.start_date).format('MMM YYYY')}` : 'N/A'}
                        </Typography>
                    )
                },
                {
                    field: 'date_range',
                    headerName: 'Date Range',
                    type: 'string',
                    flex: 1,
                    minWidth: 200,
                    renderCell: (params) => (
                        <Typography sx={{ fontSize: '0.86rem', lineHeight: 'inherit' }}>
                            {params.row.roster ? `${dayjs(params.row.roster.start_date).format('DD MMM')} - ${dayjs(params.row.roster.end_date).format('DD MMM YYYY')}` : 'N/A'}
                        </Typography>
                    )
                },
                {
                    field: 'status',
                    headerName: 'Status',
                    type: 'string',
                    flex: 0.7,
                    minWidth: 120,
                    renderCell: (params) => {
                        const statusMap = {
                            'PUBLISHED': { label: 'Published', color: 'success' },
                            'DRAFT': { label: 'Draft', color: 'warning' },
                            'ARCHIVED': { label: 'Archived', color: 'info' },
                            'PDF_GENERATED': { label: 'PDF Generated', color: 'success' },
                        };
                        const status = statusMap[params.row.roster?.status?.name] || { label: params.row.roster?.status?.name || 'N/A', color: 'default' };
                        return <StatusBall label={status.label} size="small" color={status.color} />;
                    }
                },
                {
                    field: 'created_at',
                    headerName: 'Generated At',
                    type: 'string',
                    flex: 1,
                    minWidth: 170,
                    renderCell: (params) => {
                        return (
                            <Tooltip title={dayjs(params.value).format('MMMM DD, YYYY HH:mm A')} arrow>
                                <Typography sx={{ fontSize: '0.86rem', lineHeight: 'inherit' }}>
                                    {dayjs(params.value).format('DD MMM YYYY hh:mm A')}
                                </Typography>
                            </Tooltip>
                        );
                    },
                },
                {
                    field: 'actions',
                    type: 'actions',
                    headerName: 'Action',
                    width: 100,
                    showInMenu: true,
                    getActions: (params) => {
                        const sharePointUrl = params.row.sharepoint_url;
                        
                        return [
                            <GridActionsCellItem
                                key="download"
                                icon={<DownloadIcon />}
                                label="Download PDF"
                                onClick={handleDownload(params.id, sharePointUrl)}
                                disabled={!sharePointUrl}
                            />,
                        ];
                    },
                },
            ];
        },
        [handleDownload],
    );

    return (
        <DataGrid
            loading={isLoading}
            rows={items || []}
            getRowId={(row) => row.id}
            rowHeight={60}
            columns={columns}
            pagination
            paginationMode="server"
            paginationModel={config.pagination}
            rowCount={config?.total ?? 0}
            pageSizeOptions={[5, 10, 25, 100]}
            onPaginationModelChange={(params) => !isLoading && (setIsLoading(true) || dispatch(setConfig({ pagination: { ...config.pagination, page: params.page, pageSize: params.pageSize } })))}
            initialState={{
                columns: {
                    columnVisibilityModel: {
                        id: false,
                    }
                },
                sorting: {
                    sortModel: [{ field: 'created_at', sort: 'desc' }],
                },
                pagination: { paginationModel: { ...config.pagination } }
            }}
        />
    );
};
